import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { roundLabel } from '../utils/wcLabels';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  ChevronDown, ChevronUp, Star, Check, X as XIcon, History as HistoryIcon, Trophy,
  Target, Award
} from 'lucide-react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import TeamLogo from '../components/TeamLogo';
import { shortTeamName as lastName } from '../components/teamUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function SportBadge({ sport }) {
  const cls =
    sport === 'AFL' ? 'tipster-badge-afl'
    : sport === 'NRL' ? 'tipster-badge-nrl'
    : sport === 'EPL' ? 'tipster-badge-epl'
    : sport === 'AL' ? 'tipster-badge-al'
    : 'tipster-badge-wc';
  const label = sport === 'WC' ? 'World Cup' : sport === 'AL' ? 'A-League' : sport;
  return <span className={cls}>{label}</span>;
}

function TipCell({ tip, fixture, isWC, wcLogo }) {
  if (!tip) {
    return (
      <div className="flex items-center justify-center text-gray-300 text-xs">—</div>
    );
  }
  const isCorrect = tip.is_correct === true;
  const isWrong = tip.is_correct === false;
  const pending = !fixture.is_completed;
  const bonus = tip.bonus || {};
  const anyBonus = bonus.exact_margin || bonus.closest_margin || bonus.correct_goalkicker || bonus.wc_exact_score;

  const bgClass = pending
    ? 'bg-gray-50'
    : isCorrect
    ? (anyBonus ? 'bg-yellow-50 ring-1 ring-[#FFCC00]' : 'bg-green-50 ring-1 ring-green-200')
    : 'bg-red-50 ring-1 ring-red-200';

  const txtClass = pending
    ? 'text-gray-700'
    : isCorrect
    ? 'text-green-700'
    : 'text-red-700';

  return (
    <div className={`rounded-sm px-2 py-1.5 flex items-center gap-1.5 ${bgClass}`} data-testid="matrix-tip-cell">
      <TeamLogo teamName={tip.predicted_winner} logoUrl={wcLogo} size={20} />
      <div className="flex-1 min-w-0">
        <div className={`text-xs font-semibold truncate ${txtClass}`}>
          {lastName(tip.predicted_winner)}
        </div>
        {(tip.margin_prediction != null || tip.goalkicker_prediction || tip.home_score_prediction != null) && (
          <div className="text-[10px] text-gray-500 truncate flex items-center gap-1">
            {tip.home_score_prediction != null && tip.away_score_prediction != null && (
              <span className={bonus.wc_exact_score ? 'text-[#B8860B] font-bold' : ''}>
                {tip.home_score_prediction}–{tip.away_score_prediction}
                {bonus.wc_exact_score && (
                  <Target className="inline-block w-3 h-3 ml-0.5 text-[#B8860B]" data-testid="bp-wc-exact-icon" />
                )}
              </span>
            )}
            {tip.margin_prediction != null && (
              <span className={bonus.exact_margin ? 'text-[#B8860B] font-bold' : bonus.closest_margin ? 'text-green-700 font-semibold' : ''}>
                m:{tip.margin_prediction}
                {bonus.exact_margin && (
                  <Target className="inline-block w-3 h-3 ml-0.5 text-[#B8860B]" data-testid="bp-exact-icon" />
                )}
                {bonus.closest_margin && (
                  <Check className="inline-block w-3 h-3 ml-0.5 text-green-600" strokeWidth={3} data-testid="bp-closest-icon" />
                )}
              </span>
            )}
            {tip.goalkicker_prediction && (
              <span className={bonus.correct_goalkicker ? 'text-[#B8860B] font-bold' : ''}>
                gk:{tip.goalkicker_prediction.split(' ')[0]}
                {bonus.correct_goalkicker && (
                  <Award className="inline-block w-3 h-3 ml-0.5 text-[#B8860B]" data-testid="bp-goalkicker-icon" />
                )}
              </span>
            )}
          </div>
        )}
      </div>
      {!pending && (
        <span className={`text-xs font-bold ${tip.points_earned > 0 ? (anyBonus ? 'text-[#B8860B]' : 'text-[#007AFF]') : 'text-gray-400'}`}>
          {tip.points_earned > 0 ? `+${Math.round(tip.points_earned * 100) / 100}` : '0'}
        </span>
      )}
      {isCorrect && !pending && !anyBonus && <Check className="w-3 h-3 text-green-600 flex-shrink-0" strokeWidth={3} />}
      {isCorrect && !pending && anyBonus && <Star className="w-3 h-3 text-[#FFCC00] fill-current flex-shrink-0" />}
      {isWrong && !pending && <XIcon className="w-3 h-3 text-red-600 flex-shrink-0" strokeWidth={3} />}
    </div>
  );
}

export default function HistoryPage() {
  const [sportFilter, setSportFilter] = useState('ALL');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedRounds, setExpandedRounds] = useState({});
  const [wcLogos, setWcLogos] = useState({});
  const [eplLogos, setEplLogos] = useState({});
  const [alLogos, setAlLogos] = useState({});

  useEffect(() => {
    let mounted = true;
    axios.get(`${API_URL}/api/teams/WC/logos`)
      .then(res => mounted && setWcLogos(res.data || {}))
      .catch(() => mounted && setWcLogos({}));
    axios.get(`${API_URL}/api/teams/EPL/logos`)
      .then(res => mounted && setEplLogos(res.data || {}))
      .catch(() => mounted && setEplLogos({}));
    axios.get(`${API_URL}/api/teams/AL/logos`)
      .then(res => mounted && setAlLogos(res.data || {}))
      .catch(() => mounted && setAlLogos({}));
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    setLoading(true);
    const sportParam = sportFilter === 'ALL' ? '' : `?sport=${sportFilter}`;
    axios.get(`${API_URL}/api/history-matrix${sportParam}`, { withCredentials: true })
      .then(res => {
        setData(res.data);
        // Auto-expand most recent round per sport
        const seenSports = new Set();
        const exp = {};
        (res.data.rounds || []).forEach(r => {
          if (!seenSports.has(r.sport)) {
            exp[`${r.sport}_${r.round_number}`] = true;
            seenSports.add(r.sport);
          }
        });
        setExpandedRounds(exp);
      })
      .catch(() => setData({ users: [], rounds: [] }))
      .finally(() => setLoading(false));
  }, [sportFilter]);

  const toggle = (key) => setExpandedRounds(p => ({ ...p, [key]: !p[key] }));

  const users = data?.users || [];
  // Build grid template based on user count
  const matchCol = '24rem';
  const userColWidth = '10rem';
  const gridTemplate = `${matchCol} repeat(${users.length}, minmax(${userColWidth}, 1fr))`;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <main className="max-w-[1600px] mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <HistoryIcon className="w-6 h-6 text-[#007AFF]" />
              <span className="text-xs uppercase tracking-widest text-gray-500 font-semibold">
                Tipping History
              </span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-black uppercase tracking-tighter" data-testid="history-heading">
              Match by Match
            </h1>
          </div>
          <Link to="/dashboard">
            <Button variant="outline" className="border-gray-200 hover:border-gray-300 hover:bg-gray-50">
              Back
            </Button>
          </Link>
        </div>

        {/* Sport filter */}
        <div className="flex flex-col sm:flex-row sm:items-end gap-4 mb-6">
          <div className="tipster-card max-w-sm flex-shrink-0">
            <label className="text-xs uppercase tracking-wider text-gray-500 mb-2 block">Sport</label>
            <Select value={sportFilter} onValueChange={setSportFilter}>
              <SelectTrigger className="bg-white border-gray-200" data-testid="history-sport-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white border-gray-200">
                <SelectItem value="ALL">All Sports</SelectItem>
                <SelectItem value="AFL">AFL</SelectItem>
                <SelectItem value="NRL">NRL</SelectItem>
                <SelectItem value="EPL">EPL</SelectItem>
                <SelectItem value="AL">A-League</SelectItem>
                <SelectItem value="WC">World Cup</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Legend */}
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-gray-600 px-1">
            <span className="text-[10px] uppercase tracking-widest text-gray-400 font-bold mr-1">Bonuses</span>
            <span className="inline-flex items-center gap-1.5">
              <Target className="w-3.5 h-3.5 text-[#B8860B]" />
              <span>Exact margin <span className="text-[#B8860B] font-bold">+5</span></span>
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Check className="w-3.5 h-3.5 text-green-600" strokeWidth={3} />
              <span>Closest margin <span className="text-green-700 font-bold">+1</span></span>
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Award className="w-3.5 h-3.5 text-[#B8860B]" />
              <span>Leading goalkicker <span className="text-[#B8860B] font-bold">+1</span></span>
            </span>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 border-2 border-[#007AFF] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : !data || data.rounds.length === 0 ? (
          <div className="tipster-card text-center py-16">
            <Trophy className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No tips on record yet</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="history-rounds">
            {data.rounds.map((round) => {
              const key = `${round.sport}_${round.round_number}`;
              const isOpen = !!expandedRounds[key];
              return (
                <div
                  key={key}
                  className="tipster-card p-0 overflow-hidden"
                  data-testid={`history-round-${round.sport}-${round.round_number}`}
                >
                  {/* Round header */}
                  <button
                    onClick={() => toggle(key)}
                    className="w-full flex items-center gap-4 px-5 py-4 hover:bg-gray-50 transition-colors text-left"
                    data-testid={`history-round-toggle-${key}`}
                  >
                    <SportBadge sport={round.sport} />
                    <div className="flex-1">
                      <div className="font-bold text-[#1A1A1A]">
                        {roundLabel(round.sport, round.round_number)}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {round.matches.length} match{round.matches.length !== 1 ? 'es' : ''}
                      </div>
                    </div>
                    {isOpen ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                  </button>

                  {/* Matrix */}
                  {isOpen && (
                    <div className="border-t border-gray-100 overflow-x-auto">
                      <div className="min-w-fit">
                        {/* Header row: tipster names */}
                        <div
                          className="grid gap-2 px-4 py-3 bg-gray-50 border-b border-gray-200 sticky top-0"
                          style={{ gridTemplateColumns: gridTemplate }}
                        >
                          <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Match</div>
                          {users.map(u => (
                            <div key={u.user_id} className="text-center">
                              <div className="text-xs font-bold uppercase tracking-wider text-[#1A1A1A] truncate" title={u.name}>
                                {u.name.split(' ')[0]}
                              </div>
                              <div className="text-[11px] text-gray-500 mt-0.5">
                                <span className="text-[#007AFF] font-bold">{Math.round((round.user_totals[u.user_id]?.points ?? 0) * 100) / 100}</span>
                                <span className="text-gray-400"> · {round.user_totals[u.user_id]?.correct ?? 0}/{round.user_totals[u.user_id]?.tips ?? 0}</span>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Match rows */}
                        {round.matches.map((m) => {
                          const isWC = round.sport === 'WC';
                          const isEPL = round.sport === 'EPL';
                          const isAL = round.sport === 'AL';
                          const logoMap = isWC ? wcLogos : (isEPL ? eplLogos : (isAL ? alLogos : null));
                          return (
                          <div
                            key={m.fixture_id}
                            className={`grid gap-2 px-4 py-3 border-b border-gray-50 last:border-b-0 ${
                              m.is_game_of_round ? 'bg-yellow-50/30' : ''
                            }`}
                            style={{ gridTemplateColumns: gridTemplate }}
                            data-testid={`matrix-match-${m.fixture_id}`}
                          >
                            {/* Match cell */}
                            <div className="flex items-center gap-2 min-w-0">
                              {m.is_game_of_round && (
                                <Star className="w-4 h-4 text-[#FFCC00] fill-current flex-shrink-0" title="Game of the Round" />
                              )}
                              <div className="flex items-center gap-1.5 min-w-0">
                                <TeamLogo teamName={m.home_team} logoUrl={logoMap?.[m.home_team]} size={22} />
                                <span className="text-sm font-semibold text-[#1A1A1A] truncate">{lastName(m.home_team)}</span>
                              </div>
                              <span className="text-xs text-gray-400 font-mono mx-1">v</span>
                              <div className="flex items-center gap-1.5 min-w-0">
                                <span className="text-sm font-semibold text-[#1A1A1A] truncate">{lastName(m.away_team)}</span>
                                <TeamLogo teamName={m.away_team} logoUrl={logoMap?.[m.away_team]} size={22} />
                              </div>
                              {m.is_completed && (
                                <span className="text-xs font-mono text-gray-500 ml-auto flex-shrink-0">
                                  {m.home_score}–{m.away_score}
                                </span>
                              )}
                            </div>

                            {/* User tip cells */}
                            {users.map(u => (
                              <TipCell
                                key={u.user_id}
                                tip={m.tips_by_user[u.user_id]}
                                fixture={m}
                                isWC={isWC}
                                wcLogo={logoMap && m.tips_by_user[u.user_id] ? logoMap[m.tips_by_user[u.user_id].predicted_winner] : undefined}
                              />
                            ))}
                          </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
